import React from 'react';
import ReactDOM, { hydrateRoot } from 'react-dom/client';
import './tailwind.output.css';
import './index.css';
import App from './App';
import * as serviceWorkerRegistration from './serviceWorkerRegistration';
import * as analytics from './utils/analytics';
import { startBrowserBarInset } from './utils/browserBarInset';
import { isIOS } from './utils/iosLayout';

analytics.init(process.env.REACT_APP_GA4_MEASUREMENT_ID);

// Before first render. iPhone uses the page-scroll layout; other devices keep the toolbar measurement.
if (isIOS()) {
  document.documentElement.classList.add('is-ios');
} else {
  startBrowserBarInset();
}

// Preconnect to API origin for faster first request (before first API call)
(function () {
  const apiUrl = process.env.REACT_APP_API_URL;
  if (apiUrl) {
    try {
      const u = new URL(apiUrl);
      if (u.origin !== window.location.origin) {
        const link = document.createElement('link');
        link.rel = 'preconnect';
        link.href = u.origin;
        link.crossOrigin = 'anonymous';
        document.head.appendChild(link);
      }
    } catch (_) {}
  }
})();

// Kick off startup data fetch in parallel with React boot (logged-in users only).
try {
  if (typeof window !== 'undefined' && localStorage.getItem('token')) {
    // Dynamic import keeps earlyFetch out of the absolute critical path if unused
    import('./services/earlyFetch').then((m) => m.startEarlyFetch()).catch(() => {});
  }
} catch (_) {}

const rootElement = document.getElementById('root');

// Static SEO shells (data-seo-static) are for no-JS crawlers only — clear them and
// mount with createRoot so we never hydrate mismatched markup. react-snap / true
// prerender output (without data-seo-static) can still hydrate when present.
const isSeoStaticShell = rootElement && rootElement.getAttribute('data-seo-static') === 'true';
if (isSeoStaticShell) {
  rootElement.removeAttribute('data-seo-static');
  rootElement.innerHTML = '';
}

if (rootElement.hasChildNodes()) {
  hydrateRoot(
    rootElement,
    <React.StrictMode>
      <App />
    </React.StrictMode>
  );
} else {
  const root = ReactDOM.createRoot(rootElement);
  root.render(
    <React.StrictMode>
      <App />
    </React.StrictMode>
  );
}

// Hide HTML splash as soon as React mounts; App shows its own skeletons while auth/data load.
requestAnimationFrame(() => {
  if (typeof window.__removeSplash === 'function') {
    window.__removeSplash();
  }
});

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://cra.link/PWA
serviceWorkerRegistration.register({
  onUpdate: (registration) => {
    window.dispatchEvent(
      new CustomEvent('swUpdateAvailable', { detail: { registration } })
    );
  },
});

// Progressive splash messages after HTML watchdog's 8s message (avoids fighting over the same label).
// HTML shows "Slow connection…" at 8s; these refine the copy if JS has loaded but splash is still up.
const splashMessages = [
  { delay: 14000, text: 'Almost there…' },
  { delay: 20000, text: 'Still trying — please check your internet connection' },
];

let splashMessageTimers = [];
let splashAnalyticsSent = false;

function showSplashMessage(text) {
  const msgEl = document.getElementById('splash-message');
  if (msgEl) {
    msgEl.textContent = text;
    msgEl.classList.add('visible');
  }
}

function startSplashMessages() {
  splashMessages.forEach(({ delay, text }) => {
    const timer = setTimeout(() => showSplashMessage(text), delay);
    splashMessageTimers.push(timer);
  });
}

function clearSplashMessages() {
  splashMessageTimers.forEach(clearTimeout);
  splashMessageTimers = [];
}

function reportSplashDone() {
  if (splashAnalyticsSent) return;
  splashAnalyticsSent = true;
  try {
    const splashMs = Math.round(
      typeof performance !== 'undefined' && typeof performance.now === 'function'
        ? performance.now()
        : 0
    );
    analytics.event('app_splash_done', { splash_ms: splashMs });
  } catch (_) {}
}

// Export for App.js to call when auth loading completes
window.__removeSplash = function removeSplash() {
  if (typeof window.__cancelSplashWatchdog === 'function') {
    window.__cancelSplashWatchdog();
  }
  clearSplashMessages();
  reportSplashDone();
  const splash = document.getElementById('app-splash');
  if (!splash) return;
  splash.classList.add('splash-fade-out');
  splash.addEventListener('transitionend', () => splash.remove(), { once: true });
};

// Start progressive messages
startSplashMessages();
